import * as braintree from "braintree-web";
import { useEffect, useState } from "react";

const HOSTED_FIELD_LABELS: Record<string, string> = {
	number: "Card number",
	expirationDate: "Expiration date",
	cvv: "CVV",
};

const getHostedFieldsValidationError = (
	instance: braintree.HostedFields,
): string | null => {
	const state = instance.getState();
	const missing: string[] = [];
	const invalid: string[] = [];
	for (const key of ["number", "expirationDate", "cvv"] as const) {
		const field = state.fields[key];
		if (!field) continue;
		if (field.isEmpty) {
			missing.push(HOSTED_FIELD_LABELS[key] ?? key);
		} else if (!field.isValid) {
			invalid.push(HOSTED_FIELD_LABELS[key] ?? key);
		}
	}
	if (missing.length === 0 && invalid.length === 0) {
		return null;
	}
	const parts: string[] = [];
	if (missing.length > 0) {
		parts.push(
			`${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} required`,
		);
	}
	if (invalid.length > 0) {
		parts.push(
			`${invalid.join(", ")} ${invalid.length === 1 ? "is" : "are"} invalid`,
		);
	}
	return `Please correct the credit card details: ${parts.join("; ")}.`;
};

export type BraintreeUSBankAccountPayload = {
	nonce: string;
	details: {
		bankName: string;
		accountType: string;
		ownershipType: string;
		routingNumber: string;
		lastTwo: string;
	};
	type: string;
};

export type HostedFieldsValidity = {
	number: boolean;
	expirationDate: boolean;
	cvv: boolean;
};

export type HostedFieldsEmpty = {
	number: boolean;
	expirationDate: boolean;
	cvv: boolean;
};

export type UseBraintreeReturn = {
	hostedFieldsInstance: braintree.HostedFields | null;
	usBankAccountInstance: braintree.USBankAccount | null;
	hostedFieldsValidity: HostedFieldsValidity;
	hostedFieldsEmpty: HostedFieldsEmpty;
	tokenizeCard: (options?: {
		cardholderName?: string;
	}) => Promise<braintree.HostedFieldsTokenizePayload>;
	tokenizeAch: (details: {
		routingNumber: string;
		accountNumber: string;
		accountHolderName: string;
		ownershipType: "personal" | "business";
		accountType: "checking" | "savings";
		billingAddress: {
			streetAddress: string;
			extendedAddress?: string;
			locality: string;
			region: string;
			postalCode: string;
			countryCodeAlpha2: string;
		};
	}) => Promise<BraintreeUSBankAccountPayload>;
};

export const useBraintree = (
	token: string | null | undefined,
	showForm: boolean,
	selectedMethod: "ACH" | "CreditCard" | null,
): UseBraintreeReturn => {
	const [hostedFieldsInstance, setHostedFieldsInstance] =
		useState<braintree.HostedFields | null>(null);
	const [usBankAccountInstance, setUsBankAccountInstance] =
		useState<braintree.USBankAccount | null>(null);
	const [hostedFieldsValidity, setHostedFieldsValidity] =
		useState<HostedFieldsValidity>({
			number: false,
			expirationDate: false,
			cvv: false,
		});
	const [hostedFieldsEmpty, setHostedFieldsEmpty] = useState<HostedFieldsEmpty>(
		{
			number: true,
			expirationDate: true,
			cvv: true,
		},
	);

	useEffect(() => {
		let hfInstance: braintree.HostedFields | null = null;
		let bankInstance: braintree.USBankAccount | null = null;

		// Reset validity/empty state whenever we (re-)initialize Hosted Fields.
		// Without this, switching payment method or toggling the form retains
		// stale `isValid: true` from a previous session, which would let the
		// Save button enable while the new (empty) fields are not actually
		// valid -- causing Braintree tokenize to throw.
		setHostedFieldsValidity({
			number: false,
			expirationDate: false,
			cvv: false,
		});
		setHostedFieldsEmpty({
			number: true,
			expirationDate: true,
			cvv: true,
		});

		if (token) {
			braintree.client
				.create({
					authorization: token,
				})
				.then((clientInstance) => {
					// 1. Initialize USBankAccount (does not require DOM)
					const achPromise = braintree.usBankAccount.create({
						client: clientInstance,
					});

					// 2. Initialize Hosted Fields (requires DOM)
					// Only attempt to create if CC form is shown
					let hfPromise: Promise<braintree.HostedFields | null> =
						Promise.resolve(null);

					if (showForm && selectedMethod === "CreditCard") {
						const cardNumberEl = document.querySelector("#card-number");
						if (!cardNumberEl) {
							console.warn("Card number container not found in DOM");
							return Promise.all([Promise.resolve(null), achPromise]);
						}

						hfPromise = braintree.hostedFields
							.create({
								client: clientInstance,
								styles: {
									input: {
										"font-size": "16px",
										"font-family": "sans-serif",
										color: "#333",
									},
									":focus": {
										color: "black",
									},
								},
								fields: {
									number: {
										selector: "#card-number",
										placeholder: "Card Number",
									},
									cvv: {
										selector: "#cvv",
										placeholder: "CVV",
									},
									expirationDate: {
										selector: "#expiration-date",
										placeholder: "MM/YY",
									},
								},
							})
							.catch((error: unknown) => {
								console.warn(
									"Hosted Fields setup failed:",
									error instanceof Error ? error.message : "Unknown error",
								);
								return null;
							});
					}

					return Promise.all([hfPromise, achPromise]);
				})
				.then(([hostedFields, usBankAccount]) => {
					hfInstance = hostedFields;
					bankInstance = usBankAccount;

					setHostedFieldsInstance(hostedFields);
					setUsBankAccountInstance(usBankAccount);

					if (hostedFields) {
						const updateState = (event: {
							fields: Record<string, { isValid: boolean; isEmpty: boolean }>;
						}) => {
							setHostedFieldsValidity({
								number: event.fields["number"]?.isValid ?? false,
								expirationDate:
									event.fields["expirationDate"]?.isValid ?? false,
								cvv: event.fields["cvv"]?.isValid ?? false,
							});
							setHostedFieldsEmpty({
								number: event.fields["number"]?.isEmpty ?? true,
								expirationDate: event.fields["expirationDate"]?.isEmpty ?? true,
								cvv: event.fields["cvv"]?.isEmpty ?? true,
							});
						};
						hostedFields.on("validityChange", updateState);
						hostedFields.on("blur", updateState);
						hostedFields.on("empty", updateState);
						hostedFields.on("notEmpty", updateState);
					}
				})
				.catch((error) => {
					console.error("Braintree initialization error:", error);
				});
		}

		return () => {
			if (hfInstance) {
				hfInstance.teardown();
			}
			if (bankInstance) {
				bankInstance.teardown();
			}
			setHostedFieldsInstance(null);
			setUsBankAccountInstance(null);
		};
	}, [token, showForm, selectedMethod]);

	const tokenizeCard = async (options?: {
		cardholderName?: string;
	}): Promise<braintree.HostedFieldsTokenizePayload> => {
		if (!hostedFieldsInstance) {
			throw new Error("Credit card fields are not initialized.");
		}

		// Guard against tokenizing when fields are empty or invalid. Braintree
		// throws an opaque error in that case; surface a friendlier message so
		// the user understands what to fix.
		const validationError =
			getHostedFieldsValidationError(hostedFieldsInstance);
		if (validationError) {
			throw new Error(validationError);
		}

		return hostedFieldsInstance.tokenize(options);
	};

	type BraintreeBankDetails = {
		routingNumber: string;
		accountNumber: string;
		accountHolderName: string;
		ownershipType: string;
		accountType: string;
		firstName?: string;
		lastName?: string;
		businessName?: string;
		billingAddress?: {
			streetAddress: string;
			extendedAddress?: string;
			locality: string;
			region: string;
			postalCode: string;
			countryCodeAlpha2: string;
		};
	};

	const tokenizeAch = async (details: {
		routingNumber: string;
		accountNumber: string;
		accountHolderName: string;
		ownershipType: "personal" | "business";
		accountType: "checking" | "savings";
		billingAddress: {
			streetAddress: string;
			extendedAddress?: string;
			locality: string;
			region: string;
			postalCode: string;
			countryCodeAlpha2: string;
		};
	}): Promise<BraintreeUSBankAccountPayload> => {
		if (!usBankAccountInstance) {
			throw new Error("ACH fields are not initialized.");
		}

		return new Promise<BraintreeUSBankAccountPayload>((resolve, reject) => {
			const nameParts = details.accountHolderName.trim().split(/\s+/);
			const firstName = nameParts[0] || "";
			const lastName = nameParts.slice(1).join(" ") || firstName;

			const bankDetails: BraintreeBankDetails = {
				routingNumber: details.routingNumber,
				accountNumber: details.accountNumber,
				accountHolderName: details.accountHolderName,
				ownershipType: details.ownershipType,
				accountType: details.accountType,
				billingAddress: details.billingAddress,
			};

			if (details.ownershipType === "personal") {
				bankDetails.firstName = firstName;
				bankDetails.lastName = lastName;
			} else {
				bankDetails.businessName = details.accountHolderName;
			}

			usBankAccountInstance.tokenize(
				{
					bankDetails,
					mandateText:
						"I authorize TQL to debit my bank account for the specified invoice total.",
					bankLogin: null,
				},
				(err: unknown, payload: BraintreeUSBankAccountPayload) => {
					if (err) {
						reject(err instanceof Error ? err : new Error(JSON.stringify(err)));
					} else {
						resolve(payload);
					}
				},
			);
		});
	};

	return {
		hostedFieldsInstance,
		usBankAccountInstance,
		hostedFieldsValidity,
		hostedFieldsEmpty,
		tokenizeCard,
		tokenizeAch,
	};
};
